<template>
  <div class="page-container">
    <header class="page-header">
      <div class="d-flex align-items-center">
        <portal-target name="sidebar-toggle" class="d-flex align-items-center mt-1"/>
        <h4 class="mb-0">{{ title }}</h4>
      </div>
      <div class="d-flex align-items-center">
        <b-button id="view-button" class="custom-btn-secondary mr-1" @click="toggleView">
          <font-awesome-icon :icon="viewMode === 'table' ? 'th-large' : 'list'"/>
        </b-button>
        <b-tooltip target="view-button" triggers="hover" custom-class="custom-tooltip" noninteractive>
          {{ viewMode === 'table' ? 'Канбан' : 'Таблица' }}
        </b-tooltip>
        <b-button id="table-settings" class="custom-btn-secondary mr-1" v-b-toggle="viewMode === 'table' ? 'sidebar-fields' : 'sidebar-kanban'">
          <v-icon name="buttons/cog" :size="13"/>
        </b-button>
        <b-tooltip target="table-settings" triggers="hover" custom-class="custom-tooltip" noninteractive>
          Колонки
        </b-tooltip>
        <b-btn v-if="canEditResponses()" class="custom-btn-primary" :disabled="isBusy" @click="showModal(null, false)">
          <v-icon name="buttons/plus" :size="12"/>
          Новый отклик
        </b-btn>
      </div>
    </header>

    <div class="page-body">
      <div class="page-filters">
        <filters-drawer
          :query.sync="filters.query"
          :is-busy="isBusy"
          :filter-tags="activeFilterTags"
          @remove-tag="removeFilterTag"
          @submit="onSearch"
          @clear="onClearFilters"
        >
          <b-col md="6" cols="12" class="mt-1">
            <label class="small text-muted">Объявления</label>
            <recruit-job-posting-select
              ref="recruit_job_posting_select"
              title="id объявления"
              :value="filters.job_posting_id"
              @onSelect="onSelectJobPosting"
            />
          </b-col>
          <b-col md="6" cols="12" class="mt-1">
            <label class="small text-muted">Заявка на подбор</label>
            <recruit-task-select
              ref="recruit_task_select"
              :value="filters.recruit_task_id"
              @onSelect="onSelectRecruitTask"
            />
          </b-col>
          <b-col md="6" cols="12" class="mt-1">
            <label class="small text-muted">ID исполнителя</label>
            <b-form-input
              id="input-response-worker-id"
              v-model.number="filters.worker_id"
              type="number"
              min="1"
              step="1"
              size="sm"
              placeholder="Введите ID исполнителя"
            />
          </b-col>
          <b-col md="6" cols="12" class="mt-1">
            <label class="small text-muted">Статус</label>
            <b-form-select
              id="input-response-status"
              size="sm"
              v-model="filters.status"
              :options="statusOptions"
            />
          </b-col>
          <b-col cols="12" class="mt-1">
            <label class="small text-muted">Ответственные</label>
            <user-multi-select
              id="user-multi-select-assigned"
              ref="user_multi_select_assigned"
              title="Ответственные"
              @onSelect="onSelectUserAssigned"
            />
          </b-col>
        </filters-drawer>
      </div>

      <template v-if="isBusy || (items && items.length > 0)">
        <div class="page-table-container" :class="{ 'border-0': viewMode === 'kanban' }">
          <b-table
            v-if="viewMode === 'table'"
            id="job-posting-responses"
            ref="job_posting_responses"
            class="custom-table mb-0 text-small"
            sticky-header
            selectable
            select-mode="single"
            responsive
            :busy.sync="isBusy"
            :items="items"
            :fields="renderedFields"
            no-provider-filtering
            no-provider-paging
            no-provider-sorting
            no-local-sorting
            @sort-changed="sortChanged"
            @row-dblclicked="onRowClicked"
          >
            <template #table-busy>
              <tables-skeleton :fields="renderedFields" :rows="3"/>
            </template>

            <template #cell(fits)="row">
              <b-form-checkbox
                :checked="row.item.fits === true || row.item.fits === 1 || row.item.fits === 'true'"
                disabled
                class="d-inline-block"
                aria-label="Подходит по критериям"
                @click.native.stop
              />
            </template>

            <template #cell(id)="row">
              <b-link @click.stop="onRowClicked(row.item)" class="custom-btn-secondary" style="color: #1A9167 !important; text-decoration: none !important;">
                {{ row.item.id }}
              </b-link>
            </template>

            <template #cell(recruit_task_id)="row">
              <b-link @click.stop="showModalRecruitTask(row.item.recruit_task_id)" class="custom-btn-secondary" style="color: #1A9167 !important; text-decoration: none !important;">
                {{ row.item.recruit_task_id }}
              </b-link>
            </template>

            <template #cell(job_posting_id)="row">
              <b-link @click.stop="showModalJobPosting(row.item.job_posting_id)" class="custom-btn-secondary" style="color: #1A9167 !important; text-decoration: none !important;">
                {{ row.item.job_posting_id }}
              </b-link>
            </template>

            <template #cell(assigned)="row">
              <div v-if="row.item.assigned">
                {{ getAssignedName(row.item.assigned) }}
                <span v-if="isClaimedByMe(row.item)" class="ml-1 custom-tag tag-success">Вы</span>
              </div>
              <b-button
                v-else-if="canEditResponses()"
                class="custom-btn-primary"
                :disabled="claimingResponseId !== null"
                @click.stop="takeAssignment(row.item)"
              >
                <b-spinner v-if="claimingResponseId === row.item.id" small/>
                Взять
              </b-button>
              <span v-else>—</span>
            </template>

            <template #cell(worker_id)="row">
              <b-link
                v-if="row.item.worker_id && row.item.worker_active === true && canViewWorkers()"
                @click.stop="openWorker(row.item.worker_id)"
              >
                {{ row.item.worker_id }}
              </b-link>
              <span v-else>
                {{ row.item.worker_id || '—' }}
                <span v-if="row.item.worker_id && row.item.worker_active !== true" class="ml-1 custom-tag tag-secondary">удалён</span>
              </span>
            </template>

            <template #cell(status_id)="row">
              {{ row.item.status_name || getStatusText(row.item.status_id) }}
            </template>

            <template #cell(start_date)="row">
              {{formatTableDate(row.item.start_date)}}
            </template>

            <template #cell(action)="row">
              <b-button-group>
                <b-button
                  v-if="row.item.worker_id && row.item.worker_active === true && canViewWorkers()"
                  class="custom-btn-secondary mr-1"
                  style="color: #1A9167 !important;"
                  v-b-tooltip.hover.top="{ title: 'Создать исполнителя из отклика', customClass: 'custom-tooltip' }"
                  @click.stop="openWorker(row.item.worker_id)"
                >
                  <v-icon name="navbar/user_circle" :size="13"/>
                </b-button>
                <b-button
                  v-if="canLinkWorkers() && canProcessResponse(row.item)"
                  class="mr-1"
                  size="sm"
                  variant="outline-info"
                  v-b-tooltip.hover.v-info
                  title="Привязать существующего исполнителя"
                  @click.stop="showLinkWorkerModal(row.item)"
                >
                  <font-awesome-icon icon="link"/>
                </b-button>
                <b-button
                  v-if="canEditWorkers() && canProcessResponse(row.item)"
                  class="mr-1"
                  size="sm"
                  variant="info"
                  v-b-tooltip.hover.v-info
                  title="Создать исполнителя из отклика"
                  @click.stop="showModalWorker(row.item)"
                >
                  <font-awesome-icon icon="plus"/>
                </b-button>
                <!-- TODO: ban -->
                <b-button v-if="canEditResponses()" class="custom-btn-secondary" @click="onRemove(row)" v-b-tooltip.hover.v-danger title="Удалить отклик">
                  <v-icon name="buttons/trash" :size="14"/>
                </b-button>
              </b-button-group>
            </template>
          </b-table>

          <kanban
            v-if="viewMode === 'kanban'"
            :items="items"
            :columns="activeKanbanColumns"
            column-value="status_id"
            group-name="recruit-job-postings"
            @status-updated="kanbanUpdateStatus"
          >
            <template #card="{ item }">
              <b-card
                no-body
                class="kb-card shadow-sm"
                @dblclick="onRowClicked(item)"
              >
                <div class="d-flex justify-content-between align-items-center w-100">
                  <div class="font-weight-bold text-truncate mr-2">
                    {{ item.name }}
                  </div>
                  <b-dropdown variant="link" toggle-class="text-decoration-none box-shadow-none flex-shrink-0" no-caret class="custom-dropdown p-0 m-0 custom-btn-secondary" style="width: 16px !important;">
                    <template #button-content>
                      <v-icon name="buttons/ellipsis_vertical" :size="15"/>
                    </template>
                    <b-dropdown-item variant="success" @click.stop="showModal(item.id, true)">
                      <v-icon name="buttons/eye" :size="14" class="mr-2"/>
                      Смотреть
                    </b-dropdown-item>
                    <b-dropdown-item v-if="canEditResponses()" variant="primary" @click="showModal(item.id, false)">
                      <v-icon name="buttons/edit" :size="14" class="mr-2"/>
                      Редактировать
                    </b-dropdown-item>
                    <b-dropdown-item v-if="canEditResponses()" variant="danger" @click="onRemove({ item })">
                      <v-icon name="buttons/trash" :size="14" class="mr-2"/>
                      Удалить
                    </b-dropdown-item>
                  </b-dropdown>
                </div>

                <div class="d-flex align-items-center w-100 small text-truncate">
                  <v-icon name="buttons/phone" :size="13" class="mr-2 flex-shrink-0"/>
                  <span class="text-truncate">{{ phoneString(item.phone) }}</span>
                </div>

                <div v-if="item.start_date" class="d-flex align-items-center w-100 small">
                  <v-icon name="buttons/calendar" :size="13" class="mr-2 flex-shrink-0"/>
                  <span>{{ formatTableDate(item.start_date) }}</span>
                </div>

                <div v-if="item.provider_name" class="d-flex align-items-center w-100 small text-truncate">
                  <v-icon name="integrations/avito" :size="14" class="mr-2 flex-shrink-0"/>
                  <span class="text-truncate">{{ item.provider_name }}</span>
                </div>

                <hr class="w-100 m-0 border-light"/>

                <div class="d-flex justify-content-between align-items-center w-100 small text-muted">
                  <b-button variant="link" style="color: #1A9167 !important;" class="p-0 pl-1 mr-2 text-decoration-none" @click="onRowClicked(item)">
                    Открыть
                  </b-button>
                  <div class="text-truncate">
                    <font-awesome-icon icon="user" class="mr-1"/>
                    {{ getAssignedName(item.assigned) || 'Не назначен' }}
                  </div>
                </div>
              </b-card>
            </template>
          </kanban>
        </div>

        <div class="page-pagination">
          <pagination-footer
            use-router
            :current-page.sync="currentPage"
            :total-rows="totalRows"
            :per-page.sync="perPage"
            @page-click="onPagination"
            @per-page-change="onPerPageChange"
          />
        </div>
      </template>

      <div v-else class="empty-state-container">
        <v-icon name="navbar/empty_table" :size="90"/>
        <h5>Нет созданных откликов.</h5>
        <p>Создайте новый отклик прямо сейчас</p>
        <b-btn v-if="canEditResponses()" class="custom-btn-primary mt-1" :disabled="isBusy" @click="showModal(null, false)">
          <v-icon name="buttons/plus" :size="12" class="mr-1"/>
          Новый отклик
        </b-btn>
      </div>
    </div>

    <DynamicTables
      storage-key="job_posting_responses_table_cols"
      sidebar-fields="sidebar-fields"
      :fields-model.sync="fields"
      :serviceEndField="serviceEndField"
      fieldKey="id"
    />

    <dynamic-tables
      v-if="kanbanColumns.length || viewMode === 'kanban'"
      storage-key="job_postings_responses_kanban_cols"
      sidebar-fields="sidebar-kanban"
      :fields-model="kanbanColumns"
      field-key="key"
      allow-delete
      @update:fieldsModel="kanbanColumns = $event"
      @delete-col="handleDeleteKanbanColumn"
      @sidebar-closed="isAddingColumn = false"
    >
      <template #list-append>
        <div class="position-relative">
          <b-button
            v-if="!isAddingColumn"
            variant="link"
            class="text-decoration-none d-flex align-items-center justify-content-center w-100"
            style="color: #1A9167 !important;"
            @click="isAddingColumn = true"
          >
            <v-icon name="buttons/plus" :size="13" class="mr-2"/>
            <span class="text-small">Добавить новую колонку</span>
          </b-button>

          <div v-else>
            <recruit-response-status-select
              :exclude-ids="activeColumnIds"
              @onSelect="handleAddKanbanColumn"
            />
          </div>

          <div v-if="isAddingColumn" @click.stop="isAddingColumn = false"></div>
        </div>
      </template>
    </dynamic-tables>

    <JobPostingResponse ref="job_posting_response" @refresh="getItems" @modal-closed="onModalClosed"/>

    <recruit-task ref="modal_recruit_task" :is-integrated="true"/>

    <RecruitJobPosting ref="modal_recruit_job_posting"/>

    <b-modal
      ref="modal_worker"
      body-class="p-0"
      title="Создание нового исполнителя"
      size="xl"
    >
      <Worker
        ref="worker_component"
        :is-modal="true"
        :response-id="responseId"
        @refresh="onWorkerSaved"
        @close-modal="onModalWorkerClosed"
      />
      <template #modal-footer="{}">
          <div class="justify-content-end m-0 p-0">
            <b-button size="sm" variant="secondary" class="mr-1" @click="onModalWorkerClosed()">Отмена</b-button>
            <b-button size="sm" variant="success" @click="handleSubmitModalWorker()">Сохранить</b-button>
          </div>
      </template>
    </b-modal>

    <b-modal
      ref="modal_link_worker"
      title="Привязка существующего исполнителя"
      hide-footer
      @hidden="resetLinkWorkerModal"
    >
      <b-form-group
        label="ID исполнителя"
        description="Укажите ID активного исполнителя. Отклик останется закреплён за вами."
      >
        <b-form-input
          v-model.number="linkWorkerId"
          type="number"
          min="1"
          step="1"
          :disabled="linkingWorker"
          @keyup.enter="linkSelectedWorker"
        />
      </b-form-group>
      <div class="d-flex justify-content-between align-items-center">
        <b-link v-if="canViewWorkers()" :to="{ name: 'Workers' }" target="_blank">
          Открыть список исполнителей
        </b-link>
        <span v-else/>
        <div>
          <b-button size="sm" variant="secondary" class="mr-1" :disabled="linkingWorker" @click="$refs.modal_link_worker.hide()">
            Отмена
          </b-button>
          <b-button size="sm" variant="success" :disabled="!isValidLinkWorkerId || linkingWorker" @click="linkSelectedWorker">
            <b-spinner v-if="linkingWorker" small/>
            Привязать
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import recruitJobPostings from "../../network/recruitJobPostings";
import catalogs from "../../network/catalogs";
import users from "../../network/users";
  import helpers from "../../utilites/helpers";
  import dateFormatter from "../../utilites/dateFormatter";
  import DynamicTables from "../../utilites/commonUi/DynamicTables.vue";
  import FiltersDrawer from "../../utilites/commonUi/FiltersDrawer.vue";
  import permissions, {PERM} from "../../utilites/permissions";
  import PaginationFooter from "../../utilites/commonUi/PaginationFooter.vue";
  import pageSearchService from "../../utilites/pageSearchService";
  import TablesSkeleton from "../../utilites/commonUi/TablesSkeleton.vue";
  import Kanban from "../../utilites/commonUi/Kanban.vue";
    import LocationMultiSelect from "../Locations/LocationMultiSelect.vue";
    import UserMultiSelect from "../Users/UserMultiSelect.vue";
    import JobPostingResponse from "./JobPostingResponse.vue";
    import RecruitTask from "./RecruitTask.vue";
    import RecruitJobPosting from "./RecruitJobPosting.vue";
    import Worker from "../Workers/Worker.vue";
    import RecruitJobPostingSelect from "./RecruitJobPostingSelect.vue";
    import RecruitTaskSelect from "./RecruitTaskSelect.vue";
    import RecruitResponseStatusSelect from "../Catalogs/RecruitResponseStatuses/RecruitResponseStatusSelect.vue";

export default {
  name: "RecruitJobPostings",
  components: {
    RecruitResponseStatusSelect, Kanban, TablesSkeleton, RecruitTaskSelect, RecruitJobPostingSelect, Worker, RecruitJobPosting, RecruitTask, PaginationFooter, FiltersDrawer, JobPostingResponse, UserMultiSelect, LocationMultiSelect, DynamicTables
  },
  mixins: [
    recruitJobPostings, catalogs, users,
    helpers, dateFormatter, permissions, pageSearchService
  ],
  data() {
    return {
      title: 'Отклики',
      items: [],
      usersList: [],
      sortBy: 'id',
      sortDesc: true,
      userId: null,
      serviceStartField: {
        key: 'fits',
        label: '',
        class: 'text-center',
        thStyle: { width: '40px', 'min-width': '40px' },
      },
      fields: [
        {
          key: 'id',
          label: 'ID кандидата',
          class: 'text-center',
          thStyle: { width: '60px', 'min-width': '60px' },
          sortable: true,
          visible: true,
        },
        {
          key: 'recruit_task_id',
          label: 'ID заявки',
          thStyle: { width: '60px', 'min-width': '60px' },
          class: 'text-center',
          sortable: true,
          visible: true,
        },
        {
          key: 'job_posting_id',
          label: 'ID объявления',
          thStyle: { width: '60px', 'min-width': '60px' },
          class: 'text-center',
          sortable: true,
          visible: true,
        },
        {
          key: 'worker_id',
          label: 'ID исполнителя',
          thStyle: { width: '90px', 'min-width': '90px' },
          class: 'text-center',
          sortable: true,
          visible: true,
        },
        {
          key: 'location_vacancy_name',
          label: 'Вакансия',
          thStyle: { width: '100px', 'min-width': '100px' },
          sortable: true,
          visible: true,
        },
        {
          key: 'location_name',
          label: 'Объект',
          thStyle: { width: '100px', 'min-width': '100px' },
          sortable: true,
          visible: true,
        },
        {
          key: 'assigned',
          label: 'Рекрутер',
          thStyle: { width: '120px', 'min-width': '120px' },
          class: 'text-center',
          sortable: true,
          visible: true,
        },
        {
          key: 'status_id',
          label: 'Статус',
          thStyle: { width: '90px', 'min-width': '90px' },
          sortable: true,
          visible: true,
        },
        // TODO: start_date переименовать
        {
          key: 'start_date',
          label: 'Дата отклика',
          thStyle: { width: '100px', 'min-width': '100px' },
          class: 'text-center',
          sortable: true,
          visible: true,
        },
        /* TODO: авито интеграция
        {
          key: 'provider_name',
          label: 'Сервис',
          sortable: true,
          visible: true,
        },
         */
      ],
      serviceEndField: {
        key: 'action',
        label: 'Действия',
        class: 'text-center',
        thStyle: { width: '80px', 'min-width': '80px' },
      },
      isBusy: false,
      filters: this.getDefaultFilters(),
      responseStatuses: [],
      providerOptions: [
        { value: null, text: 'Все сервисы' },
        { value: 1, text: 'Авито' },
        { value: 2, text: 'HH' },
      ],
      viewMode: 'table',
      kanbanColumns: [],
      isAddingColumn: false,
      deleteRow: {
        id: null,
      },
      responseId: null,
      claimingResponseId: null,
      linkResponseId: null,
      linkWorkerId: null,
      linkingWorker: false,
    }
  },
  async mounted() {
    await this.configureRefs();
  },
  async created() {
    this.isBusy = true;
    const query = this.$route.query;

    if (query) {
      if (query.query) this.filters.query = query.query;

      if (query.job_posting_id) {
        this.filters.job_posting_id = Number(query.job_posting_id);
      }

      if (query.recruit_task_id) {
        this.filters.recruit_task_id = Number(query.recruit_task_id);
      }

      if (query.worker_id) {
        this.filters.worker_id = Number(query.worker_id);
      }

      if (query.assigned) {
        const decodeLoc = decodeURIComponent(query.assigned);
        this.filters.assigned = decodeLoc.split(",");
      }

      if (query.status) {
        this.filters.status = Number(query.status);
      }
    }

    if (this.$route.hash) {
      this.currentPage = this.getPage(this.$route.hash);
    }

    this.usersList = (await this.getUsersSearch({})).data.items;
    this.userId = (await this.getUserInfo({})).data.id;

    const statusesResult = await this.getRecruitResponseStatuses({});
    this.responseStatuses = statusesResult && statusesResult.data ? statusesResult.data.items : [];
    this.syncKanbanColumns();

    await this.onSearch();
  },
  computed: {
    visibleFields() {
      return this.fields.filter(field => field.visible);
    },
    renderedFields() {
      const allFields = [
        { ...this.serviceStartField, sticky: 'left' },
        ...this.visibleFields,
        { ...this.serviceEndField, sticky: 'right' }
      ];
      return this.handleFixedCols(allFields);
    },
    isValidLinkWorkerId() {
      return Number.isInteger(Number(this.linkWorkerId)) && Number(this.linkWorkerId) > 0;
    },
    statusOptions() {
      return [
        { value: null, text: 'Все статусы' },
        ...this.responseStatuses.map(status => ({
          value: status.id,
          text: status.active ? status.name : `${status.name} (неактивен)`,
        })),
      ];
    },
    activeKanbanColumns() {
      if (!this.kanbanColumns) return [];

      return this.kanbanColumns
        .filter(c => c.visible)
        .map(c => ({
          id: c.key,
          title: c.label,
          class: c.class
        }));
    },
    activeColumnIds() {
      return this.kanbanColumns.map(c => c.key);
    },
    activeFilterTags() {
      const tags = [];
      const defaults = this.getDefaultFilters();

      if (this.filters.job_posting_id) {
        tags.push({ key: 'job_posting_id', label: `ID объявления: ${this.filters.job_posting_id}` });
      }

      if (this.filters.assigned.length > 0) {
        tags.push({ key: 'assigned', label: `Рекрутеры: ${this.filters.assigned.length}`,});
      }

      if (this.filters.recruit_task_id) {
        tags.push({ key: 'recruit_task_id', label: `ID заявки: ${this.filters.recruit_task_id}` });
      }

      if (this.filters.worker_id) {
        tags.push({ key: 'worker_id', label: `ID исполнителя: ${this.filters.worker_id}` });
      }

      if (this.filters.status !== defaults.status) {
        const foundItem = this.statusOptions.find(s => s.value === this.filters.status);
        tags.push({ key: 'status', label: foundItem ? foundItem.text : 'Статус отклика' });
      }

      return tags;
    },
  },
  watch: {
    '$route.params.id': {
      handler(newId) {
        this.$nextTick(() => {
          if (newId && this.$refs.job_posting_response) {
            const isViewonly = this.$route.query.mode === 'view';
            this.$refs.job_posting_response.showModal(newId, isViewonly);
          } else if (!newId && this.$refs.job_posting_response) {
            this.$refs.job_posting_response.hideModal();
          }
        });
      }, immediate: true
    },
    responseStatuses: {
      handler() {
        this.syncKanbanColumns();
      }, deep: true
    }
  },
  methods: {
    async takeAssignment(item) {
      if (!this.canEditResponses() || item.assigned || this.claimingResponseId !== null) return;

      this.claimingResponseId = item.id;
      const result = await this.claimResponse(item.id);

      if (result && !result.error) {
        this.showToast('Вы успешно взяли отклик в работу.', 'Успех', 'success');
      } else if (result && result.status === 409) {
        this.showError('Отклик уже взят или изменён другим пользователем.');
      } else {
        this.showError('Не удалось взять отклик в работу.');
      }

      await this.getItems();
      this.claimingResponseId = null;
    },
    async getItems() {
      const data = {
        query: this.filters.query,
        page: this.currentPage,
        size: this.perPage,
        job_posting_id: this.filters.job_posting_id,
        recruit_task_id: this.filters.recruit_task_id,
        worker_id: this.filters.worker_id,
        assigned: this.filters.assigned,
        status: this.filters.status,
        sortBy: [this.sortBy],
        sortDesc: [this.sortDesc],
      }

      this.isBusy = true;
      const result = await this.getResponses(data);
      this.isBusy = false;

      if (!result || !result.data) {
        this.showError("Что-то пошло не так. Попробуйте обновить страницу и повторить действие.");
        this.items = [];
        this.totalRows = 0;
        this.isBusy = false;
        return;
      }

      this.items = result.data.items;
      this.totalRows = result.data.total;
      this.isBusy = false;
    },
    showModal(responseId, isViewonly) {
      const canEdit = this.canEditResponses();

      if (!responseId) {
        if (!canEdit) return;

        this.$refs.job_posting_response.showModal(null, false);
        return;
      }

      const isView = isViewonly || !canEdit;
      const query = isView ? { mode: 'view' } : {};

      this.$router.push({
        name: 'JobPostingResponses',
        params: { id: responseId },
        query: query
      });
    },
    showModalRecruitTask(recruitTaskId) {
      if (!recruitTaskId) return;

      const canEdit = this.canEditRecruitTasks();
      const isViewonly = !canEdit;

      this.$refs.modal_recruit_task.showModal(recruitTaskId, isViewonly);
    },
    showModalJobPosting(jobPostingId) {
      if (!jobPostingId) return;

      const canEdit = this.canEditRecruitJobPostings();
      const isViewonly = !canEdit;

      this.$refs.modal_recruit_job_posting.showModal(jobPostingId, isViewonly);
    },
    showModalWorker(item) {
      if (!this.canEditWorkers() || !this.canProcessResponse(item)) return;

      this.responseId = item.id;
      this.$refs.modal_worker.show();

      this.$nextTick(() => {
        if (this.$refs.worker_component) {
          this.$refs.worker_component.configure(item);
        }
      });
    },
    onModalWorkerClosed() {
      this.responseId = null;
      this.$refs.modal_worker.hide();
    },
    async handleSubmitModalWorker() {
      if (this.$refs.worker_component) {
       await this.$refs.worker_component.handleSubmit();
      }
    },
    async onWorkerSaved() {
      this.$bvToast.toast('Исполнитель успешно связан с откликом.', {
        title: 'Успех',
        variant: 'success',
        autoHideDelay: 3000,
        solid: true
      });
      await this.getItems();
      this.onModalWorkerClosed();
    },
    isClaimedByMe(item) {
      return item && item.assigned != null && Number(item.assigned) === Number(this.userId);
    },
    canProcessResponse(item) {
      return this.canEditResponses()
        && this.isClaimedByMe(item)
        && (!item.worker_id || item.worker_active !== true);
    },
    openWorker(workerId) {
      if (!workerId || !this.canViewWorkers()) return;

      this.$router.push({
        name: 'WorkersView',
        params: { worker_id: workerId },
      }).catch(()=>{});
    },
    showLinkWorkerModal(item) {
      if (!this.canLinkWorkers() || !this.canProcessResponse(item)) return;

      this.linkResponseId = item.id;
      this.linkWorkerId = null;
      this.$refs.modal_link_worker.show();
    },
    resetLinkWorkerModal() {
      this.linkResponseId = null;
      this.linkWorkerId = null;
      this.linkingWorker = false;
    },
    async linkSelectedWorker() {
      if (!this.isValidLinkWorkerId || !this.linkResponseId || this.linkingWorker) return;

      this.linkingWorker = true;
      const result = await this.linkResponseToWorker(this.linkResponseId, Number(this.linkWorkerId));

      if (result && !result.error) {
        this.showToast('Исполнитель успешно связан с откликом.', 'Успех', 'success');
        this.$refs.modal_link_worker.hide();
        await this.getItems();
      } else if (result && result.status === 404) {
        this.showError('Активный исполнитель с таким ID не найден.');
      } else if (result && result.status === 409) {
        this.showError('Отклик уже связан, не взят вами или был изменён другим пользователем.');
        await this.getItems();
      } else {
        this.showError('Не удалось связать исполнителя с откликом.');
      }

      this.linkingWorker = false;
    },
    onRowClicked(row) {
      this.showModal(row.id, false);
    },
    onModalClosed() {
      if (this.$route.params.id) {
        this.$router.replace({
          name: 'JobPostingResponses',
          params: { id: null },
          hash: this.$route.hash,
        }).catch(()=>{});
      }
    },
    getAssignedName(userId) {
      if (!userId) return null;

      const user = this.usersList.find(u => u.id === userId);
      if (!user) { return '' }
      return user.name;
    },
    getStatusText(statusValue) {
      const option = this.statusOptions.find(opt => opt.value === statusValue);
      return option ? option.text : statusValue;
    },
    // Filters
    getDefaultFilters() {
      return {
        query: '',
        job_posting_id: null,
        recruit_task_id: null,
        worker_id: null,
        status: null,
        assigned: [],
      };
    },
    async onClearFilters() {
      const filters = this.getDefaultFilters();

      if (JSON.stringify(this.filters) === JSON.stringify(filters)) return;

      this.filters = filters;
      await this.configureRefs();
      await this.onSearch();
    },
    removeFilterTag(tag) {
      if (tag.key === 'job_posting_id') {
        this.filters.job_posting_id = null;
        this.$refs.recruit_job_posting_select.onClear();
      }
      else if (tag.key === 'assigned') {
        this.filters.assigned = [];
        this.$refs.user_multi_select_assigned.onClearSelected();
      }
      else if (tag.key === 'recruit_task_id') {
        this.filters.recruit_task_id = null;
      }
      else if (tag.key === 'worker_id') {
        this.filters.worker_id = null;
      }
      else if (tag.key === 'status') {
        this.filters.status = null;
      }

      this.onSearch();
    },
    async configureRefs() {
      if (this.$refs.user_multi_select_assigned != null) {
        await this.$refs.user_multi_select_assigned.configure(this.filters.assigned);
      }
    },
    onSelectJobPosting(jobPostingId) {
      this.filters.job_posting_id = jobPostingId || null;
    },
    onSelectRecruitTask(recruitTaskId) {
      this.filters.recruit_task_id = recruitTaskId || null;
    },
    onSelectUserAssigned(users) {
      this.filters.assigned = users.map(item => { return item.id; });
    },
    onRemove(row) {
      if (!this.canEditResponses()) return;

      if (row.item) {
        this.deleteRow = row.item
      }

      const title = "Удаление отклика";
      const message = `Вы действительно хотите отклик №${this.deleteRow.id}?`;
      this.showModalDelete(title, message, async () => {
        const result = await this.deleteResponse(this.deleteRow);

        if (result.error) {
          this.showToast('Ошибка при удалении.', 'Ошибка', 'danger');
        } else {
          this.showToast('Отклик удалён.', 'Успех', 'success');
          await this.getItems();
        }
      });
    },
    // Permissions
    canEditResponses() {
      return this.hasPermission([PERM.RECRUIT_CANDIDATE_EDIT]);
    },
    canEditRecruitJobPostings() {
      return this.hasPermission([PERM.RECRUIT_JOB_POSTING_EDIT]);
    },
    canEditRecruitTasks() {
      return this.hasPermission([PERM.RECRUIT_TASK_EDIT]);
    },
    canEditWorkers() {
      return this.hasPermission([PERM.WORKER_EDIT]);
    },
    canLinkWorkers() {
      return [PERM.WORKER, PERM.WORKER_READ, PERM.WORKER_EDIT]
        .some(permission => this.hasPermission([permission]));
    },
    canViewWorkers() {
      return this.hasPermission([PERM.WORKER, PERM.WORKER_READ]);
    },
    // Kanban
    syncKanbanColumns(newColId = null) {
      const deletedCols = JSON.parse(localStorage.getItem('recruit_response_deleted_cols') || '[]');

      this.kanbanColumns = this.responseStatuses
        .filter(status => !deletedCols.includes(status.id))
        .map((status, index) => {
          const existing = this.kanbanColumns.find(c => c.key === status.id);
          const isVisible = (status.id === newColId) || (existing ? existing.visible : true);

          return {
            key: status.id,
            label: status.name,
            visible: isVisible,
            class: this.getKanbanBadgeClass(index)
          };
        });
    },
    handleAddKanbanColumn(selectedStatus) {
      if (!selectedStatus) return;
      this.isAddingColumn = false;

      let deletedCols = JSON.parse(localStorage.getItem('recruit_response_deleted_cols') || '[]');
      deletedCols = deletedCols.filter(id => id !== selectedStatus.id);
      localStorage.setItem('recruit_response_deleted_cols', JSON.stringify(deletedCols));

      if (!this.responseStatuses.find(s => s.id === selectedStatus.id)) {
        this.responseStatuses.push(selectedStatus);
      }

      this.syncKanbanColumns(selectedStatus.id);
    },
    handleDeleteKanbanColumn(field) {
      const index = this.kanbanColumns.findIndex(c => c.key === field.key);

      if (index !== -1) {
        this.kanbanColumns.splice(index, 1);
      }

      let deletedCols = JSON.parse(localStorage.getItem('recruit_response_deleted_cols') || '[]');

      if (!deletedCols.includes(field.key)) {
        deletedCols.push(field.key);
        localStorage.setItem('recruit_response_deleted_cols', JSON.stringify(deletedCols));
      }
    },
    toggleView() {
      this.viewMode = this.viewMode === 'table' ? 'kanban' : 'table';
    },
    getKanbanBadgeClass(index) {
      const classes = ['tag-secondary', 'tag-info', 'tag-primary', 'tag-success', 'tag-warning'];
      return classes[index % classes.length];
    },
    async kanbanUpdateStatus({ item, newStatus, oldStatus }) {
      const response = this.items.find(t => t.id === item.id);
      response.status_id = newStatus;

      const result = await this.putRecruitResponseStatus(response);

      if (result.error || !result.data) {
        response.status_id = oldStatus;
        this.showError('Не удалось обновить статус.');
        await this.getItems();
      } else {
        this.showToast('Статус отклика обновлён.', 'Успех', 'success');
      }
    },
  },
}
</script>

<style>
#job-posting-responses td {
  vertical-align: middle;
}
</style>



// WEBPACK FOOTER //
// src/components/Recruiting/JobPostingResponses.vue